<template>
    <base-modal hide-footer hide-header size="lg" @close="$emit('close')">
        <div class="col-12">
            <h3 v-if="!light || !light.id" class="editor-header">
                {{ $t('street_lights.add') }}
            </h3>
            <h3 v-else class="editor-header">
                {{ $t('street_lights.edit') }}
            </h3>
        </div>
        <div class="col-sm-12 nopads" @click.stop="null">
            <div v-if="light" class="col-sm-12 container editor-form">
                <div class="col-12">
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="photosVisible = !photosVisible">
                                {{ $t('power_stations.photos') }} ({{ light && light.photos ? light.photos.length : 0 }})
                            </h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="photosVisible = !photosVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: photosVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: photosVisible }">
                        <div class="stat-container nopads mb-3">
                            <div v-if="light.photos && light.photos.length > 0" class="col-12 nopads">
                                <Flicking ref="flicking"
                                          :options="options"
                                          :plugins="plugins"
                                          class="pointer nopads text-center"
                                          style="height: 15em;"
                                          v-if="light.photos && light.photos.length > 0"
                                >
                                    <ImageView
                                        v-for="photo in light.photos" :key="photo.id"
                                        ref="image"
                                        class="flicking-panel mx-1"
                                        :contain="true"
                                        :url="getPhotoUrl(streetLightPhotoUrl, photo)"
                                        :show-delete-button="true"
                                        @click="openImage"
                                        @deleteImage="setPhotoForDeletion"
                                        :controlSize="`sm`"
                                    />
                                    <template #viewport>
                                        <div class="flicking-pagination"></div>
                                    </template>
                                </Flicking>
                            </div>
                            <div v-else
                                 style="padding-top: 6em; height: 15em; text-align: center">
                                {{ $t('power_stations.no_photos') }}
                            </div>
                        </div>

                        <!-- Photo editor-->
                        <div class="col-12">
                            <!-- Show selected photos -->
                            <div
                                v-for="(file, index) in photos"
                                :key="index"
                                class="d-flex align-items-center justify-content-between mb-3"
                            >
                                <span>{{ file?.name || file }}</span>
                                <button
                                    class="btn btn-outline-danger "
                                    type="button"
                                    @click="removePhoto(index)"
                                >
                                    {{ $t('common.remove') }}
                                </button>
                            </div>

                            <!-- Hidden file input -->
                            <input
                                ref="photoInput"
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                class="visually-hidden"
                                @change="handlePhotoChange"
                            />

                            <!-- Custom "Add photo" button -->
                            <div class="col-12 d-flex justify-content-end">
                                <button
                                    class="btn btn-outline-primary "
                                    type="button"
                                    @click="triggerPhotoInput"
                                >
                                    {{ $t('power_stations.add_photo') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <hr>
                <h4 class="form-header">{{ $t('street_lights.basic_info') }}</h4>
                <div class="col-12">
                    <span class="span-title">{{ $t('street_lights.position') }}</span>
                    <div v-if="light.id && light.id > 0" class="nopads">
                        <div v-if="light.road_number">{{ light.road_number }} / {{ light.road_section }}</div>
                        <div>{{ light.street }} {{ light.city }}</div>
                        <div>{{ light.distance }} m</div>
                    </div>
                    <span v-else style="font-size: .85em">{{ coordinate.lat.toFixed(6) + ' ' + coordinate.lng.toFixed(6) }}</span>
                </div>
                <div class="row px-2">
                    <div class="col-sm-6 p-1">
                        <span class="span-title">{{ $t('street_lights.location_name') }}</span>
                        <input
                            id="lightLocation"
                            v-model="light.location_name"
                            required
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-sm-6 p-1">
                        <span class="span-title">{{ $t('street_lights.station') }}</span>
                        <select
                            id="lightStation"
                            v-model="selectedStation"
                            class="form-select form-select-sm"
                            :class="{ 'is-invalid': !selectedStation }"
                        >
                            <option :value="null">{{ $t('street_lights.select_station') }}</option>
                            <option v-for="station in stations" :key="station.id" :value="station.value">{{ station.text }}</option>
                        </select>
                    </div>
                </div>
                <div class="row px-2">
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('street_lights.status') }}</span>
                        <select
                            id="lightStatus"
                            v-model="light.status"
                            class="form-select form-select-sm"
                        >
                            <option :value="null">{{ $t('street_lights.select_status') }}</option>
                            <option v-for="status in statusOptions" :key="status.value" :value="status.value">{{ status.text }}</option>
                        </select>
                    </div>
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('street_lights.lighting_class') }}</span>
                        <select
                            id="lightClass"
                            v-model="light.lighting_class"
                            class="form-select form-select-sm"
                        >
                            <option v-for="classOption in lightingClassOptions" :key="classOption.value" :value="classOption.value">{{ classOption.text }}</option>
                        </select>
                    </div>
                    <div class="col-sm-4 p-1">
                        <span class="span-title">{{ $t('street_lights.construction_year') }}</span>
                        <input
                            id="lightYear"
                            v-model="light.construction_year"
                            type="number"
                            step="any"
                            min="0"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <hr>
                <h4 class="form-header mt-0 pt-0">{{ $t('power_stations.contract') }}</h4>
                <span class="tip-light">{{ $t('power_stations.contract_tip') }}</span>
                <open-contract-filter
                    :to="toDate"
                    :from="fromDate"
                    :contract="preselectedContract"
                    :show-company="false"
                    :show-title="false"
                    style="margin-left: -2px; margin-right: -3px;"
                    @loading="loading=true"
                    @loading-completed="loading=false"
                    @contract-selected="setContract"
                />
                <hr>
                <div class="col-12">
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="lightInfoVisible = !lightInfoVisible">{{ $t('street_lights.light_info') }}</h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="lightInfoVisible = !lightInfoVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: lightInfoVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: lightInfoVisible }">
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.light_type') }}</span>
                                <select id="lightType" v-model="light.light_type" class="form-select form-select-sm">
                                    <option v-for="type in lightTypeOptions" :key="type.value" :value="type.value">{{ type.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.power_rating') }}</span>
                                <input id="powerRating" v-model="light.power_rating" type="number" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.light_manufacturer') }}</span>
                                <input id="lightManufacturer" v-model="light.light_manufacturer" type="text" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.model') }}</span>
                                <input id="lightModel" v-model="light.model" type="text" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.installation') }}</span>
                                <select id="lightInstallation" v-model="light.installation" class="form-select form-select-sm">
                                    <option v-for="installation in installOptions" :key="installation.value" :value="installation.value">{{ installation.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.optics') }}</span>
                                <input id="optics" v-model="light.optics" type="text" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.dimming_profile') }}</span>
                                <input type="checkbox" id="dimmingProfile" v-model="light.dimming_profile" class="form-check-input" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.bulb_type') }}</span>
                                <select id="lightLampType" v-model="light.bulb_type" class="form-select form-select-sm">
                                    <option v-for="bulbType in bulbTypeOptions" :key="bulbType.value" :value="bulbType.value">{{ bulbType.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.bulb_count') }}</span>
                                <input id="bulbCount" v-model="light.bulb_count" type="text" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.bulb_manufacturer') }}</span>
                                <input id="bulbManufacturer" v-model="light.bulb_manufacturer" type="text" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.guarantee_basis') }}</span>
                                <select id="guaranteeBasis" v-model="light.guarantee_basis" class="form-select form-select-sm">
                                    <option v-for="guarantee in guaranteeOptions" :key="guarantee.value" :value="guarantee.value">{{ guarantee.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.guarantee_expiration') }}</span>
                                <input id="guaranteeExp" v-model="light.guarantee_expiration" type="date" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.guarantor') }}</span>
                                <input id="guarantor" v-model="light.guarantor" type="text" class="form-control form-control-sm" />
                            </div>
                        </div>
                    </div>
                </div>
                <hr>
                <div class="col-12">
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="poleVisible = !poleVisible">{{ $t('street_lights.pole_info') }}</h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="poleVisible = !poleVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: poleVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: poleVisible }">
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.pole_type') }}</span>
                                <select id="poleType" v-model="light.pole_type" class="form-select form-select-sm">
                                    <option v-for="poleType in poleTypeOptions" :key="poleType.value" :value="poleType.value">{{ poleType.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.pole_manufacturer') }}</span>
                                <input id="poleManufacturer" v-model="light.pole_manufacturer" type="text" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-12 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.pole_additional_info') }}</span>
                                <textarea
                                    id="poleAdditionalInfo"
                                    v-model="light.pole_info"
                                    class="form-control form-control-sm"
                                    :placeholder="$t('street_lights.info')"
                                    rows="4"
                                    style="resize: vertical;"
                                ></textarea>
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.stand') }}</span>
                                <input id="stand" v-model="light.stand" type="text" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.installation_height') }}</span>
                                <input id="installHeight" v-model="light.installation_height" type="number" step="any" min="0" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-4 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.supporting_structure') }}</span>
                                <select id="supportStruct" v-model="light.supporting_structure" class="form-select form-select-sm">
                                    <option v-for="supportOption in poleSupportOptions" :key="supportOption.value" :value="supportOption.value">{{ supportOption.text }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.arm_type') }}</span>
                                <select id="armType" v-model="light.arm_type" class="form-select form-select-sm">
                                    <option v-for="armType in armTypeOptions" :key="armType.value" :value="armType.value">{{ armType.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.arm_length') }}</span>
                                <input id="armLength" v-model="light.arm_length" type="number" step="any"  min="0" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.cabling') }}</span>
                                <select id="cabling" v-model="light.cabling" class="form-select form-select-sm">
                                    <option v-for="cablingOption in cablingOptions" :key="cablingOption.value" :value="cablingOption.value">{{ cablingOption.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.earthing') }}</span>
                                <select id="earthing" v-model="light.earthing" class="form-select form-select-sm">
                                    <option v-for="earthingOption in earthingOptions" :key="earthingOption.value" :value="earthingOption.value">{{ earthingOption.text }}</option>
                                </select>
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.pole_grounding_value') }}</span>
                                <input id="groundValue" v-model="light.ground_value" type="number" step="any"  min="0" class="form-control form-control-sm" />
                            </div>
                        </div>
                    </div>
                </div>
                <hr>
                <div class="col-12 nopads">
                    <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                        <div class="flex-grow-1">
                            <h4 class="form-header pointer hover-header" @click.stop="maintenanceVisible = !maintenanceVisible">{{ $t('street_lights.maintenance_info') }}</h4>
                        </div>
                        <div class="ms-2 px-2" @click.stop="maintenanceVisible = !maintenanceVisible">
                            <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: maintenanceVisible }"/>
                        </div>
                    </div>
                    <div class="routa-collapse" :class="{ collapsed: maintenanceVisible }">
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.bulb_replaced_on') }}</span>
                                <input id="bulbReplace" v-model="light.bulb_replaced_on" type="date" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('street_lights.last_maintenance') }}</span>
                                <input id="lastMaintenance" v-model="light.last_maintenance" type="date" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="row px-2">
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('power_stations.short_circuit_current') }}</span>
                                <input v-model="light.short_circuit_current" type="number" class="form-control form-control-sm" />
                            </div>
                            <div class="col-sm-6 nopads p-1">
                                <span class="span-title">{{ $t('power_stations.short_circuit_current_measure_date') }}</span>
                                <input v-model="light.short_circuit_current_measure_date" type="date" class="form-control form-control-sm" />
                            </div>
                        </div>
                        <div class="col-12">
                            <span class="span-title">{{ $t('street_lights.other_usage') }}</span>
                            <textarea
                                id="otherUsage"
                                v-model="light.other_usage"
                                class="form-control form-control-sm"
                                :placeholder="$t('street_lights.other_usage')"
                                rows="4"
                                style="resize: vertical;"
                            ></textarea>
                        </div>
                        <div class="col-12">
                            <span class="span-title">{{ $t('street_lights.hazards') }}</span>
                            <textarea
                                id="hazards"
                                v-model="light.hazards"
                                class="form-control form-control-sm"
                                :placeholder="$t('street_lights.hazards')"
                                rows="4"
                                style="resize: vertical;"
                            ></textarea>
                        </div>
                    </div>
                </div>
                <hr>
                <h4 class="form-header mt-0 pt-0">{{ $t('street_lights.info') }}</h4>
                <textarea
                    id="info"
                    v-model="light.info"
                    class="form-control form-control-sm"
                    :placeholder="$t('street_lights.info')"
                    rows="4"
                    style="resize: vertical;"
                ></textarea>
                <div class="col-sm-12 d-flex justify-content-end pt-4 pb-0 mb-0">
                    <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                        {{ $t('common.cancel') }}
                    </button>
                    <button class="btn btn-success form-button" :disabled="loading || light === null" @click.stop="submit">
                        {{ $t('common.save') }}
                    </button>
                </div>
            </div>
            <div v-if="loading" id="loader" class="spinner"></div>
        </div>
        <!-- Fullscreen photo -->
        <Transition name="fade">
            <fullscreen-image v-if="fullScreenPhotoData" :large-images="fullScreenPhotoData" :panel-index="panelIndex" @close="closePhoto" />
        </Transition>
    </base-modal>
</template>


<script>
import {restApi} from '../mixins/RestApiMixin'
import BaseModal from '../BaseModal'
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import {timeUtils} from "@/components/mixins/TimeUtils";
import FullscreenImage from "@/components/view/FullscreenImage";
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import {streetLightHelper} from "@/components/mixins/StreetLightMixin";
import {Pagination} from "@egjs/flicking-plugins";
import Flicking from "@egjs/vue3-flicking";
import "@egjs/flicking-plugins/dist/pagination.css";
import "@egjs/vue3-flicking/dist/flicking.css";
import ImageView from "@/components/view/ImageView.vue";
import {inject, toRaw} from "vue";

export default {
    name: 'StreetLightWizard',
    components: {FullscreenImage, Flicking, OpenContractFilter, BaseModal, ImageView},
    mixins: [restApi, timeUtils, attachmentHelper, streetLightHelper],
    props: {
        selectedContract: {
            type: Object,
            default: null
        },
        coordinate: {
            type: Object,
            default: null
        },
        streetLight: {
            type: Object,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            alertDismissSecs: 5,
            alertDismissCountDown: 0,
            alertMessage: '',
            light: null,
            contract: null,
            fullScreenPhotoData: null,
            photosVisible: true,
            lightInfoVisible: false,
            poleVisible: false,
            maintenanceVisible: false,
            photos: [],
            photoDeletionIds: [],
            selectedStation: null,
            stations: [],
            fromDate: new Date().toISOString(),
            toDate: new Date().toISOString(),
            variant: 'danger',
            plugins: [new Pagination({ type: 'bullet' })],
            options: {
                preventDefaultOnDrag: true,
                circular: true,
                panelsPerView: 1
            },
            panelIndex: 0
        }
    },
    emits: ['close', 'closeAndUpdate'],
    computed: {
        preselectedContract() {
            return this.light && this.light.contract ? this.light.contract : null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted() {
        this.fetchStations()
        if (!this.streetLight) {
            this.light = {
                id: null,
                street: null,
                city: null,
                location_name: null,
                road_number: null,
                road_section: null,
                distance: null,
                construction_year: null,
                last_maintenance: null,
                lighting_class: null,
                light_manufacturer: null,
                light_type: null,
                arm_length: null,
                arm_type: null,
                bulb_count: null,
                bulb_manufacturer: null,
                bulb_replaced_on: null,
                bulb_type: null,
                guarantee_basis: null,
                guarantee_expiration: null,
                guarantor: null,
                hatch_count: null,
                installation_height: null,
                pole_model: null,
                pole_type: null,
                pole_structure: null,
                supporting_structure: null,
                stand: null,
                power_station: null,
                status: null,
                contract: null,
                info: '',
                short_circuit_current: null,
                short_circuit_current_measure_date: null,
                position: this.coordinate,
                earthing: null,
                cabling: null,
                hazards: null,
                otherUsage: null
            }
        } else {
            this.light = toRaw(this.streetLight)
            this.light.short_circuit_current_measure_date = this.timestampToDateInput(this.light.short_circuit_current_measure_date)
            this.light.bulb_replaced_on = this.timestampToDateInput(this.light.bulb_replaced_on)
            this.light.guarantee_expiration = this.timestampToDateInput(this.light.guarantee_expiration)
            this.light.last_maintenance = this.timestampToDateInput(this.light.last_maintenance)
            this.contract = this.light.contract ? this.light.contract : null
            this.selectedStation = this.light.power_station.id
            this.fromDate = this.light.created_at? this.light.created_at : this.fromDate
        }
    },
    methods: {
        triggerPhotoInput() {
            this.$refs.photoInput?.click();
        },
        handlePhotoChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.photos.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            this.photos.splice(index, 1);
        },
        fetchStations() {
            this.loading = true
            this.stations = []
            this.restFetch(this.powerStationUrl, this.handleSearch, () => {
                this.loading = false
            })
        },
        handleSearch: function (response) {
            this.loading = false
            if (response && response.data) {
                response.data.forEach(station => {
                    this.stations.push({value: station.id, text: station.id + ' ' + station.location_name})
                })
            }
        },
        submit: function () {
            this.loading = true
            if (this.contract) {
                this.light.contract = this.contract
            }
            if (!this.selectedStation) {
                this.showAlert(this.$t('street_light.error_station'))
                return;
            } else {
                this.light.power_station = {id: this.selectedStation}
            }
            if (!this.light.position) {
                this.showAlert(this.$t('street_light.error_position'))
                return;
            } else if (this.coordinate) {
                // Coordinates given as props for new light
                this.light.position = {
                    lat: this.coordinate.lat,
                    lon: this.coordinate.lng
                }
            }
            if (this.light.id && this.light.id > 0) {
                this.restUpdate(this.streetLightUrl, this.light, this.success, this.fail)
            } else {
                this.restAdd(this.streetLightUrl, this.light, this.success, this.fail)
            }
        },
        success: function (response) {
            let photos = this.getValidAttachments(this.photos)
            if (photos.length > 0) {
                this.uploadPhoto(photos, response.id)
            } else {
                this.uploadCompleted()
            }
        },
        uploadPhoto: function (photos, id) {
            if (photos.length > 0) {
                this.loading = true
                let formData = new FormData()
                photos.forEach(photoFile => {
                    if (photoFile !== undefined && photoFile !== null) {
                        formData.append('photos[]', photoFile)
                    }
                })
                this.restPostWithFile(this.streetLightPhotoUrl  + '/' + id, {}, formData, this.uploadCompleted, this.fail)
            }
        },
        uploadCompleted: function () {
            this.loading = false
            if(this.photoDeletionIds && this.photoDeletionIds.length > 0) {
                this.deletePhotos()
            } else {
                this.showAlert(this.$t('common.alert_update_success'), 'success')
                this.$emit('closeAndUpdate')
            }
        },
        deletePhotos() {
            this.loading = true
            this.restDeleteItem(this.streetLightPhotoUrl + "/" + this.light.id, {photo_ids: this.photoDeletionIds}, this.handleDeleteResponse)
        },
        handleDeleteResponse: function () {
            this.loading = false
            this.photoDeletionIds = []
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('closeAndUpdate')
        },
        setPhotoForDeletion(photoId) {
            const id = Number(photoId);
            this.photoDeletionIds.push(id)
            // Handle the logic for deleting a photo here
            this.light.photos = this.light.photos.filter(photo => photo.id !== id);
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_add_failed'))
        },
        setContract: function (contract) {
            this.contract = contract
        },
        getPhotoUrl: function (baseUrl, photo) {
            return baseUrl + '/' + this.light.id + '/' + photo.id
        },
        openImage: function () {
            const flicking = this.$refs.flicking
            const imageData = this.$refs.image
            this.fullScreenPhotoData = []
            imageData.forEach(src => {
                this.fullScreenPhotoData.push(src)
            })
            this.panelIndex = flicking.index

        },
        closePhoto() {
            this.fullScreenPhotoData = undefined
        }
    }
}
</script>
<style>
@import "../../styles/modal.css";
</style>
